import { Container } from '@almbrand/container';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import classNames from 'classnames';
import { Component } from 'components/Component';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { useFormSubmit, useModal } from 'hooks';
import { useContext, useEffect, useState } from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormResetField,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';
import { useAppDispatch } from 'store/hooks';
import { setDisplayGlobalError } from 'store/slices/errorSlice';
import { meetDependency } from '../../../services/DependencyService';
import { getComponentProperties } from '../../../services/MapFieldService';
import { isPackageStepByPageId } from '../../../services/PackageService';
import { SystemIconsComponent } from '../SystemIconsComponent';
import styles from './ModalContentComponent.module.scss';

export interface ModalContentComponentProps extends FieldsBlock {
	resetField?: UseFormResetField<FieldValues>;
	register?: UseFormRegister<FieldValues>;
	watch?: UseFormWatch<FieldValues>;
	isValid?: boolean;
	hasError?: boolean;
	errors?: FieldErrors<FieldValues>;
	trigger?: UseFormTrigger<FieldValues>;
	errorMessage?: string;
	flowStop?: boolean;
	action?: { title?: string; subtitle?: string; content?: string };
	linkText?: string;
	infoIcon?: boolean;
	subtitle?: string;
	fields?: Fields;
	pageId?: string;
	contentType?: string;
	contentId?: number;
	isTextColorWhite?: boolean;
	linkTextAlignment?: 'left' | 'center' | 'right';
}
export const ModalContentComponent: React.FC<ModalContentComponentProps> = ({
	resetField,
	register,
	watch = (e) => {
		return undefined;
	},
	isValid,
	errors,
	trigger,
	errorMessage,
	flowStop,
	contentDependency,
	action,
	linkText,
	infoIcon,
	title,
	subtitle,
	content,
	fields,
	pageId,
	isTextColorWhite,
	linkTextAlignment = 'left',
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext;
	const themeName = ThemeNames[theme];

	const { displayModal } = useModal();
	const [stepError, setStepError] = useState<string>();
	const dependentField = watch(contentDependency?.dependsOn?.uid);
	const { resetFormField } = useFormSubmit();
	const dispatch = useAppDispatch();
	const isPackages = isPackageStepByPageId(parseInt(pageId));

	const { isMobile } = useDeviceQuery();

	useEffect(() => {
		if (!dependentField || !(action?.content || action?.title || action?.subtitle)) {
			return;
		}
		if (meetDependency(contentDependency, dependentField.value)) {
			displayModal(action.title, action.subtitle, action.content);
			if (flowStop) {
				setStepError(errorMessage);
				resetField(dependentField.id);
				resetFormField(dependentField.id);
				dispatch(setDisplayGlobalError(true));
			}
		} else {
			dispatch(setDisplayGlobalError(false));
			setStepError(undefined);
		}
	}, [dependentField]);

	const mapComponents = () => {
		return fields?.items?.map((contentItem, index) => {
			const name = contentItem.content.uniqueIdentifier?.uid ?? 'NO_UNIQUE_ID_FOR_CONTENT_ITEM';

			// these formFields (if they are such) will be part of the form and therefore part of form validation!
			// This behavior we will have to look into
			const updatedComponentProps = getComponentProperties(pageId, contentItem, index, {
				register,
				resetField,
				watch,
				isValid,
				hasError: !!errors?.[name],
				errors,
				trigger,

				// submitStep: () => handleSubmit(onSubmit)(), // insert this if onchange submit form
			});

			return updatedComponentProps ? (
				<Component
					key={contentItem.content.contentId}
					{...updatedComponentProps}
				/>
			) : undefined;
		});
	};

	return (
		<>
			{(!isPackages || (isPackages && !isMobile)) && linkText && (
				<Container
					mobileWidth={isMobile ? 'fullWidth' : '342'}
					tabletWidth={isMobile ? 'fullWidth' : '996'}
					desktopWidth={isMobile ? 'fullWidth' : '996'}
					noPadding={!isMobile}
				>
					<button
						type='button'
						className={classNames(
							styles.ModalContentComponent__link,
							(isPackages || isTextColorWhite) && styles.ModalContentComponent__link__packages,
							styles[`ModalContentComponent__link--align-${linkTextAlignment}`]
						)}
						onClick={(e) => {
							e.preventDefault();
							displayModal(title, subtitle, content, mapComponents());
						}}
					>
						{
							<>
								<div>{linkText}</div>
								{infoIcon && (
									<SystemIconsComponent
										icon='Info'
										height='16'
										themeName={themeName}
									/>
								)}
							</>
						}
					</button>
				</Container>
			)}
			{action?.title && stepError && (
				<p
					role='alert'
					className={styles.ModalContentComponent__error}
				>
					{stepError}
				</p>
			)}
		</>
	);
};
